import React, { useState, useEffect, useRef } from 'react';
import { aiAPI } from '../services/api';
import { toast } from 'react-toastify';
import Icon from './Icon';

const FAB_STYLE = {
  position: 'fixed',
  bottom: '24px',
  right: '24px',
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  background: 'var(--primary-color)',
  color: '#fff',
  border: 'none',
  boxShadow: 'var(--shadow-xl)',
  cursor: 'pointer',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '22px'
};

const PANEL_STYLE = {
  position: 'fixed',
  bottom: '92px',
  right: '24px',
  width: '370px',
  maxWidth: 'calc(100vw - 32px)',
  height: '520px',
  maxHeight: 'calc(100vh - 120px)',
  background: 'var(--card-background)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-xl)',
  zIndex: 1001,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const scrollRef = useRef(null);

  const isAuthed = !!localStorage.getItem('token');

  useEffect(() => {
    if (open && !config) {
      aiAPI.getConfig()
        .then((res) => setConfig(res.data))
        .catch(() => setConfig({ enabled: false, keyConfigured: false }));
    }
  }, [open, config]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  if (!isAuthed) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const res = await aiAPI.chat(next);
      const data = res.data;
      const toolChips = (data.toolCalls || [])
        .map((t) => t.name)
        .filter(Boolean);
      setMessages([
        ...next,
        {
          role: 'assistant',
          content: data.message || '(no response)',
          tools: toolChips
        }
      ]);
      setSessionStarted(true);
    } catch (err) {
      toast.error(err.message || 'AI request failed');
      setMessages([...next, { role: 'assistant', content: '⚠️ ' + (err.message || 'Request failed') }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionStarted(false);
  };

  return (
    <>
      <button
        style={FAB_STYLE}
        onClick={() => setOpen((o) => !o)}
        aria-label="Open AI Assistant"
        title="OUK AI Assistant"
      >
        <Icon name={open ? 'close' : 'help'} />
      </button>

      {open && (
        <div style={PANEL_STYLE}>
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--primary-color)',
              color: '#fff'
            }}
          >
            <div style={{ fontWeight: 600, fontSize: '14px' }}>
              <Icon name="help" /> OUK AI Assistant
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {sessionStarted && (
                <button onClick={reset} style={btnGhost} title="New chat">
                  <Icon name="refresh" />
                </button>
              )}
              <button onClick={() => setOpen(false)} style={btnGhost} title="Close">
                <Icon name="close" />
              </button>
            </div>
          </div>

          {config && (!config.enabled || !config.keyConfigured) && (
            <div style={{ padding: '12px 14px', fontSize: '13px', color: 'var(--text-secondary)', background: '#fffbeb', borderBottom: '1px solid var(--border-color)' }}>
              AI is not enabled or no API key is configured. Set it up in <b>Settings → AI / OpenRouter</b>.
            </div>
          )}

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {messages.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                Ask about recruiters, payments, admissions, or request a draft message. The assistant can use live system data.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                <div
                  style={{
                    padding: '8px 10px',
                    borderRadius: '10px',
                    background: m.role === 'user' ? 'var(--primary-color)' : 'var(--background-color)',
                    color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                    border: m.role === 'user' ? 'none' : '1px solid var(--border-color)',
                    fontSize: '13px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {m.content}
                </div>
                {m.tools && m.tools.length > 0 && (
                  <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {m.tools.map((t, j) => (
                      <span key={j} style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--background-color)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '1px 6px' }}>
                        🔧 {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: 'flex-start', fontSize: '13px', color: 'var(--text-muted)' }}>
                Assistant is thinking…
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid var(--border-color)', padding: '10px', display: 'flex', gap: '8px' }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Type a message…"
              style={{ flex: 1, padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '13px' }}
            />
            <button onClick={send} disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }}>
              <Icon name="send" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const btnGhost = {
  background: 'transparent',
  border: 'none',
  color: '#fff',
  cursor: 'pointer',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center'
};

const btnPrimary = {
  background: 'var(--primary-color)',
  border: 'none',
  color: '#fff',
  borderRadius: 'var(--radius-md)',
  padding: '0 12px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};
